import { memo } from 'react'
import type { CSSProperties } from 'react'
import { Filter, Copy, ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'
import { textMuted } from '@/lib/tokens'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/shared/components/ui/context-menu'
import { toast } from '@/shared/hooks'
import type { PlatformEvent, EventCategory, EventOrigin } from '../types'
import {
  categoryColors,
  categoryHexColors,
  getEventTypeLabel,
  formatRelativeTime,
  formatCategoryLabel,
  formatOriginLabel,
} from '../types'
import { formatEvent, type EmphasisTone } from '../lib/eventFormatters'
import { timelineTimestamp, ingestLagSeconds, hasSignificantLag } from '../lib/eventTimestamps'

/**
 * Format an ingest lag duration (in seconds) as a compact human-readable
 * string for the card disclosure. Matches the EventRow implementation.
 */
function formatLagDuration(lagSeconds: number): string {
  const abs = Math.abs(lagSeconds)
  if (abs >= 365 * 24 * 3600) {
    const years = (abs / (365 * 24 * 3600)).toFixed(1)
    return `${years} year${parseFloat(years) !== 1 ? 's' : ''}`
  }
  if (abs >= 30 * 24 * 3600) {
    const months = Math.round(abs / (30 * 24 * 3600))
    return `${months} month${months !== 1 ? 's' : ''}`
  }
  if (abs >= 24 * 3600) {
    const days = Math.round(abs / (24 * 3600))
    return `${days} day${days !== 1 ? 's' : ''}`
  }
  if (abs >= 3600) {
    const hours = Math.round(abs / 3600)
    return `${hours} hour${hours !== 1 ? 's' : ''}`
  }
  const minutes = Math.round(abs / 60)
  return `${minutes} minute${minutes !== 1 ? 's' : ''}`
}

/**
 * Parse a URN source to get a friendly consumer name
 * e.g., "urn:kodeon:service:support" -> "Support"
 * e.g., "urn:kodeon:consumer:web-app" -> "Web App"
 */
function parseConsumerName(source: string | undefined): string | null {
  if (!source) return null
  // URN format: urn:kodeon:type:name or just a plain string
  const parts = source.split(':')
  if (parts.length >= 4) {
    // Get the last part and format it nicely
    const name = parts[parts.length - 1]
    return name
      .split(/[-_]/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  }
  // Fallback: return the source as-is if it's short, otherwise truncate
  if (source.length <= 20) return source
  return source.slice(0, 17) + '...'
}

/**
 * Extract app/consumer product name from event data
 * Checks multiple locations where app identity can appear:
 * - origin.consumer_id (e.g., "con_app_fitness_buddy_home_gym_workout_ios")
 * - app_id at top level (e.g., "app_habitminder_ios")
 * - consumer_sightings keys as fallback
 */
function parseAppName(event: PlatformEvent): string | null {
  // Try to get origin data from event.data.object
  const dataObj = event.data?.object as Record<string, unknown> | undefined
  if (!dataObj) return null

  // 1. Check for nested origin object with consumer_id
  const origin = dataObj.origin as Record<string, unknown> | undefined
  const consumerId = origin?.consumer_id as string | undefined
  if (consumerId) {
    return formatConsumerId(consumerId)
  }

  // 2. Check for top-level app_id (e.g., "app_habitminder_ios")
  const appId = dataObj.app_id as string | undefined
  if (appId) {
    return formatAppId(appId)
  }

  // 3. Fallback: check consumer_sightings keys
  const sightings = dataObj.consumer_sightings as Record<string, unknown> | undefined
  if (sightings) {
    const firstKey = Object.keys(sightings)[0]
    if (firstKey) return formatConsumerId(firstKey)
  }

  return null
}

/**
 * Format a consumer_id into a readable app name
 * e.g., "con_app_fitness_buddy_home_gym_workout_ios" -> "Fitness Buddy"
 * e.g., "con_web_acme_dashboard" -> "Acme Dashboard"
 */
function formatConsumerId(consumerId: string): string | null {
  if (!consumerId) return null

  // Remove prefix (con_app_, con_web_, con_sdk_, etc.)
  let name = consumerId.replace(/^con_(app|web|sdk|service)_/, '')

  // Remove platform suffix (_ios, _android, _web)
  name = name.replace(/_(ios|android|web)$/, '')

  // Take the first 2-3 meaningful words to keep it short
  const words = name.split('_')
  const meaningfulWords = words.slice(0, 3)

  // Format nicely
  return meaningfulWords
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Format an app_id into a readable app name
 * e.g., "app_habitminder_ios" -> "Habitminder"
 * e.g., "app_fitness_tracker_android" -> "Fitness Tracker"
 */
function formatAppId(appId: string): string | null {
  if (!appId) return null

  // Remove "app_" prefix
  let name = appId.replace(/^app_/, '')

  // Remove platform suffix (_ios, _android, _web)
  name = name.replace(/_(ios|android|web)$/, '')

  // Split and format words
  const words = name.split('_')

  // Format nicely
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
}

/**
 * Format a Unix timestamp as a 24-hour clock time including seconds.
 * e.g., 1714820445 -> "14:30:45"
 *
 * Used alongside `formatRelativeTime` so operators can see both the wall-clock
 * time the event occurred AND how long ago it was — relative-only timestamps
 * become ambiguous when correlating events across systems.
 */
function formatClockTime(timestamp: number): string {
  if (!timestamp || !Number.isFinite(timestamp) || timestamp <= 0) return ''
  const date = new Date(timestamp * 1000)
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

/**
 * Format a Unix timestamp as a long-form date+time string for the timestamp
 * tooltip. Includes weekday/date/time/zone so operators can reason about
 * events that crossed a day boundary or came from a non-local timezone.
 */
function formatFullTimestamp(timestamp: number): string {
  if (!timestamp || !Number.isFinite(timestamp) || timestamp <= 0) return ''
  const date = new Date(timestamp * 1000)
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZoneName: 'short',
  })
}

/**
 * Map an emphasis tone from the formatter to colour tokens. Used to give
 * the secondary label visual weight when it's an error, warning, etc.
 *
 * The neutral / undefined case keeps the original muted styling so the
 * unaffected majority of events look identical to before.
 */
function emphasisStyle(tone: EmphasisTone | undefined): CSSProperties {
  switch (tone) {
    case 'error':
      return {
        background: 'rgba(239, 68, 68, 0.1)',
        borderColor: 'rgba(239, 68, 68, 0.3)',
        color: 'var(--status-error, #ef4444)',
      }
    case 'warning':
      return {
        background: 'rgba(245, 158, 11, 0.1)',
        borderColor: 'rgba(245, 158, 11, 0.3)',
        color: 'var(--status-warning, #f59e0b)',
      }
    case 'success':
      return {
        background: 'rgba(34, 197, 94, 0.08)',
        borderColor: 'rgba(34, 197, 94, 0.25)',
        color: 'var(--status-success, #22c55e)',
      }
    case 'info':
      return {
        background: 'rgba(59, 130, 246, 0.08)',
        borderColor: 'rgba(59, 130, 246, 0.25)',
        color: 'var(--status-info, #3b82f6)',
      }
    default:
      return {}
  }
}

export interface EventCardFilterHandlers {
  onSourceFilter?: (source: string) => void
  onOriginFilter?: (origin: EventOrigin) => void
  onCategoryFilter?: (category: EventCategory) => void
  onSessionFilter?: (sessionId: string) => void
  onTypeFilter?: (type: string) => void
  /**
   * Filter the events list to only this customer's events. Distinct from
   * `onCustomerClick`, which is the stronger "navigate to / focus on this
   * customer" affordance — `onCustomerFilter` is a lighter "narrow the list"
   * action surfaced from the right-click menu. Omit in views where the
   * customer is already fixed (e.g. the customer detail timeline).
   */
  onCustomerFilter?: (customerId: string) => void
  /**
   * Navigate to the full customer detail page. Surfaced from the right-click
   * menu as the heaviest "drill into this customer" action — distinct from
   * `onCustomerClick` (which typically opens an inline panel) and
   * `onCustomerFilter` (which just narrows the events list). Omit in views
   * where the customer is already fixed.
   */
  onOpenCustomerProfile?: (customerId: string) => void
}

interface EventCardProps extends EventCardFilterHandlers {
  event: PlatformEvent
  onCustomerClick: (customerId: string) => void
  onClick?: (event: PlatformEvent) => void
  isNew?: boolean
}

export const EventCard = memo(function EventCard({
  event,
  onCustomerClick,
  onClick,
  onSourceFilter,
  onOriginFilter,
  onCategoryFilter,
  onSessionFilter,
  onTypeFilter,
  onCustomerFilter,
  onOpenCustomerProfile,
  isNew,
}: EventCardProps) {
  const categoryColor = categoryColors[event.category] || 'var(--text-muted)'
  const hexColor = categoryHexColors[event.category] || textMuted
  const consumerName = parseConsumerName(event.source)
  const appName = parseAppName(event)
  const formatted = formatEvent(event)
  const secondaryEmphasisStyle = emphasisStyle(formatted.emphasis)

  // Ingest lag disclosure — shown only when occurred_at differs from created
  // by more than SIGNIFICANT_LAG_SECONDS (60s).
  const showLagDisclosure = hasSignificantLag(event)
  const lagDuration = showLagDisclosure ? formatLagDuration(ingestLagSeconds(event)) : null

  // Left-click anywhere on the card opens the detail modal — except on the
  // customer button, which is the one explicit "navigate elsewhere" affordance
  // on the card. Filter actions live on the right-click context menu instead.
  const handleCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-card-stop-click]')) return
    onClick?.(event)
  }

  const handleCustomerClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (event.customer_id) onCustomerClick(event.customer_id)
  }

  // Whether any "Filter to…" rows are available for this event in this view.
  const hasFilterActions = Boolean(
    onTypeFilter ||
      onCategoryFilter ||
      onOriginFilter ||
      (onSourceFilter && event.source) ||
      (onCustomerFilter && event.customer_id) ||
      (onSessionFilter && event.session_id),
  )

  // Copy helpers — write to clipboard and surface a small toast confirmation.
  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast({ title: 'Copied', description: `${label} copied to clipboard.` })
    } catch (err) {
      console.error('Clipboard write failed:', err)
      toast({ title: 'Copy failed', description: 'Could not copy to clipboard.' })
    }
  }

  const cardBody = (
    <div
      style={{
        ...styles.card,
        ...(onClick ? { cursor: 'pointer' } : {}),
      }}
      className={cn('event-card', isNew && 'event-card-new')}
      onClick={handleCardClick}
    >
      {/* Category indicator with glow */}
      <div style={styles.categoryIndicator}>
        <div
          style={{
            ...styles.categoryDot,
            background: categoryColor,
            boxShadow: `0 0 8px ${hexColor}40`,
          }}
        />
      </div>

      {/* Event type and details */}
      <div style={styles.eventInfo}>
        <div style={styles.eventType}>{getEventTypeLabel(event.type)}</div>
        <div style={styles.eventDetails}>
          {/* Primary detail (name, amount, agent, etc.) — extracted by the
              type-aware formatter so each event family gets the most useful
              field for that category surfaced first. */}
          {formatted.primary && (
            <span style={styles.summaryPrimary} title={formatted.primary}>
              {formatted.primary}
            </span>
          )}
          {/* Secondary detail (status / outcome / error). Carries an
              emphasis tone that paints the badge red for errors, amber for
              warnings, etc. — this is one of the highest-value at-a-glance
              cues we can give an operator. */}
          {formatted.secondary && (
            <span
              style={{ ...styles.summarySecondary, ...secondaryEmphasisStyle }}
              title={formatted.secondary}
            >
              {formatted.secondary}
            </span>
          )}
          {/* Type-specific badges — payment method, channel, model, etc. */}
          {formatted.badges?.map((badge, idx) => (
            <span
              key={`badge-${idx}`}
              style={styles.summaryBadge}
              title={badge.title || badge.label}
            >
              {badge.label}
            </span>
          ))}
          {event.resource_name && !formatted.primary && (
            <span style={styles.resourceName}>{event.resource_name}</span>
          )}
          {event.session_id && (
            <span style={styles.sessionId} title={`Session: ${event.session_id}`}>
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="9" y1="3" x2="9" y2="21" />
              </svg>
              {event.session_id.slice(0, 8)}
            </span>
          )}
        </div>
      </div>

      <div style={styles.rightSection}>
        {/* App badge */}
        <div style={styles.badgeColumn100}>
          {appName && (
            <div style={styles.appBadge} title={`Consumer app: ${appName}`}>
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <path d="M12 8v8" />
                <path d="M8 12h8" />
              </svg>
              <span style={styles.truncateSmall}>{appName}</span>
            </div>
          )}
        </div>

        {/* Consumer badge */}
        <div style={styles.badgeColumn100}>
          {consumerName && (
            <div style={styles.sourceBadge} title={`Source: ${event.source}`}>
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
              <span style={styles.truncateSmall}>{consumerName}</span>
            </div>
          )}
        </div>

        {/* Customer cell — the one click target on the card that does NOT
            open the detail modal. Marked with data-card-stop-click so the
            card's handleCardClick guard ignores clicks that originate here.
            When the formatter extracted a human-friendly customer label
            (name / email / phone) we stack it on top of the raw ID. The
            raw ID stays visible because operators copy it frequently — the
            right-click menu's "Copy Customer ID" remains the primary way
            to grab it. */}
        <div style={styles.badgeColumn140} data-card-stop-click>
          {event.customer_id ? (
            <button
              style={styles.customerButton}
              className="customer-button"
              onClick={handleCustomerClick}
              title={
                formatted.customerLabel
                  ? `${formatted.customerLabel} — ${event.customer_id}`
                  : `View customer: ${event.customer_id}`
              }
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <span style={styles.customerColumn}>
                {formatted.customerLabel && (
                  <span style={styles.customerLabelText} title={formatted.customerLabel}>
                    {formatted.customerLabel}
                  </span>
                )}
                <span
                  style={
                    formatted.customerLabel
                      ? styles.customerIdSubtext
                      : styles.customerIdText
                  }
                  title={event.customer_id}
                >
                  {event.customer_id.slice(0, 16)}
                </span>
              </span>
            </button>
          ) : (
            <div style={styles.systemBadge}>
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
              </svg>
              <span>System</span>
            </div>
          )}
        </div>

        {/* Origin badge */}
        <div style={styles.badgeColumn80}>
          <div style={styles.originBadge} title={`Origin: ${event.origin}`}>
            {formatOriginLabel(event.origin)}
          </div>
        </div>

        {/* Category badge */}
        <div style={styles.badgeColumn80}>
          <div
            style={{
              ...styles.categoryBadge,
              background: `${hexColor}15`,
              borderColor: `${hexColor}30`,
              color: categoryColor,
            }}
            title={`Category: ${event.category}`}
          >
            {formatCategoryLabel(event.category)}
          </div>
        </div>

        {/* Timestamp — clock time on top, relative ("5s ago") below.
            Keyed on occurred_at (the true action time) with created fallback.
            Showing both lets operators correlate events with external systems
            (logs, dashboards) without losing the "how recent" glance value.
            When ingest lag is significant, a third line discloses it. */}
        <div
          style={styles.timestamp}
          title={
            showLagDisclosure
              ? `Occurred: ${formatFullTimestamp(timelineTimestamp(event))}\nRecorded: ${formatFullTimestamp(event.created)}`
              : formatFullTimestamp(timelineTimestamp(event))
          }
        >
          <span style={styles.timestampClock}>
            {formatClockTime(timelineTimestamp(event))}
          </span>
          <span style={styles.timestampRelative}>
            {formatRelativeTime(timelineTimestamp(event))}
          </span>
          {showLagDisclosure && lagDuration && (
            <span style={styles.lagDisclosure}>
              recorded {lagDuration} later
            </span>
          )}
        </div>
      </div>
    </div>
  )

  // Primary navigation actions go at the top of the menu — they're the
  // strongest "go somewhere" affordances, distinct from filters (which narrow
  // the current view) and copy actions.
  const showOpenCustomerProfile = Boolean(onOpenCustomerProfile && event.customer_id)

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{cardBody}</ContextMenuTrigger>
      <ContextMenuContent>
        {showOpenCustomerProfile && (
          <>
            <ContextMenuItem
              onSelect={() => onOpenCustomerProfile!(event.customer_id!)}
            >
              <ExternalLink size={12} />
              <span style={menuItemStyles.primaryLabel}>View Customer</span>
            </ContextMenuItem>
            <ContextMenuSeparator />
          </>
        )}

        {hasFilterActions && (
          <>
            <ContextMenuLabel>Filter to…</ContextMenuLabel>
            {onTypeFilter && (
              <ContextMenuItem onSelect={() => onTypeFilter(event.type)}>
                <Filter size={12} />
                <span style={menuItemStyles.label}>Type</span>
                <span style={menuItemStyles.value} title={event.type}>
                  {event.type}
                </span>
              </ContextMenuItem>
            )}
            {onCategoryFilter && (
              <ContextMenuItem onSelect={() => onCategoryFilter(event.category)}>
                <Filter size={12} />
                <span style={menuItemStyles.label}>Category</span>
                <span style={menuItemStyles.value}>{formatCategoryLabel(event.category)}</span>
              </ContextMenuItem>
            )}
            {onOriginFilter && (
              <ContextMenuItem onSelect={() => onOriginFilter(event.origin)}>
                <Filter size={12} />
                <span style={menuItemStyles.label}>Origin</span>
                <span style={menuItemStyles.value}>{formatOriginLabel(event.origin)}</span>
              </ContextMenuItem>
            )}
            {onSourceFilter && event.source && (
              <ContextMenuItem onSelect={() => onSourceFilter(event.source!)}>
                <Filter size={12} />
                <span style={menuItemStyles.label}>Source</span>
                <span style={menuItemStyles.value} title={event.source}>
                  {consumerName || event.source}
                </span>
              </ContextMenuItem>
            )}
            {onCustomerFilter && event.customer_id && (
              <ContextMenuItem onSelect={() => onCustomerFilter(event.customer_id!)}>
                <Filter size={12} />
                <span style={menuItemStyles.label}>Customer</span>
                <span style={menuItemStyles.value} title={event.customer_id}>
                  {event.customer_id.slice(0, 16)}…
                </span>
              </ContextMenuItem>
            )}
            {onSessionFilter && event.session_id && (
              <ContextMenuItem onSelect={() => onSessionFilter(event.session_id!)}>
                <Filter size={12} />
                <span style={menuItemStyles.label}>Session</span>
                <span style={menuItemStyles.value} title={event.session_id}>
                  {event.session_id.slice(0, 8)}…
                </span>
              </ContextMenuItem>
            )}
            <ContextMenuSeparator />
          </>
        )}

        <ContextMenuLabel>Copy</ContextMenuLabel>
        <ContextMenuItem onSelect={() => copyToClipboard(event.id, 'Event ID')}>
          <Copy size={12} />
          <span style={menuItemStyles.label}>Event ID</span>
          <span style={menuItemStyles.value} title={event.id}>
            {event.id}
          </span>
        </ContextMenuItem>
        {event.customer_id && (
          <ContextMenuItem
            onSelect={() => copyToClipboard(event.customer_id!, 'Customer ID')}
          >
            <Copy size={12} />
            <span style={menuItemStyles.label}>Customer ID</span>
            <span style={menuItemStyles.value} title={event.customer_id}>
              {event.customer_id}
            </span>
          </ContextMenuItem>
        )}
        <ContextMenuItem
          onSelect={() =>
            copyToClipboard(JSON.stringify(event, null, 2), 'Event JSON')
          }
        >
          <Copy size={12} />
          <span style={menuItemStyles.label}>Event as JSON</span>
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  )
})

const styles: Record<string, CSSProperties> = {
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '12px 16px',
    background: 'var(--surface-card)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-subtle)',
    backdropFilter: 'blur(4px)',
  },
  categoryIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    flexShrink: 0,
  },
  categoryDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    transition: 'all var(--transition-fast)',
  },
  eventInfo: {
    flex: 1,
    minWidth: 0,
  },
  eventType: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-primary)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  eventDetails: {
    marginTop: '2px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  resourceName: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  summaryPrimary: {
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '320px',
  },
  summarySecondary: {
    padding: '1px 6px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '11px',
    fontWeight: 500,
    color: 'var(--text-muted)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '180px',
  },
  summaryBadge: {
    padding: '1px 6px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '10px',
    fontWeight: 500,
    color: 'var(--text-faint)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '120px',
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
  },
  sessionId: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '11px',
    color: 'var(--text-faint)',
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexShrink: 0,
    justifyContent: 'flex-end',
  },
  badgeColumn100: {
    width: '100px',
    display: 'flex',
    justifyContent: 'center',
  },
  badgeColumn140: {
    width: '140px',
    display: 'flex',
    justifyContent: 'center',
  },
  badgeColumn80: {
    width: '80px',
    display: 'flex',
    justifyContent: 'center',
  },
  appBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 8px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
    fontSize: '10px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
  },
  sourceBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 8px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
    fontSize: '10px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
  },
  truncateSmall: {
    maxWidth: '65px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  customerButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 8px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '11px',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
    minWidth: 0,
  },
  customerColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    minWidth: 0,
    lineHeight: 1.15,
  },
  customerLabelText: {
    fontFamily: 'var(--font-sans)',
    fontSize: '11px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    width: '95px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  customerIdText: {
    width: '95px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  customerIdSubtext: {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '10px',
    color: 'var(--text-faint)',
    width: '95px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  systemBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 8px',
    background: 'var(--surface-elevated-strong)',
    border: '1px solid transparent',
    borderRadius: 'var(--radius-md)',
    fontSize: '11px',
    color: 'var(--text-muted)',
  },
  originBadge: {
    padding: '4px 8px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
    fontSize: '10px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
  },
  categoryBadge: {
    padding: '4px 8px',
    borderRadius: 'var(--radius-md)',
    fontSize: '10px',
    fontWeight: 500,
    border: '1px solid',
  },
  timestamp: {
    width: '90px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '2px',
    fontFamily: "'JetBrains Mono', monospace",
    fontVariantNumeric: 'tabular-nums',
  },
  timestampClock: {
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    lineHeight: 1.1,
  },
  timestampRelative: {
    fontSize: '10px',
    color: 'var(--text-muted)',
    lineHeight: 1.1,
  },
  lagDisclosure: {
    fontSize: '9px',
    fontStyle: 'italic',
    color: 'var(--text-faint)',
    lineHeight: 1.1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}

const menuItemStyles: Record<string, CSSProperties> = {
  label: {
    minWidth: '64px',
    color: 'var(--text-muted)',
    fontSize: '12px',
  },
  value: {
    flex: 1,
    minWidth: 0,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '11px',
    color: 'var(--text-primary)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'right',
  },
  primaryLabel: {
    flex: 1,
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-primary)',
  },
}
